import { InfoTooltip } from '../InfoTooltip.js';
import { METRIC_EXPLANATIONS, getConfidenceLabel } from '../../data/metric-explanations.js';
import { getBeginnerRiskLabel } from '../../lib/riskExplanation.js';

export interface SafetySummaryData {
  /** Safety score on 0-10 scale */
  score: number;
  riskLevel: string;
  /** Confidence on 0-100 scale */
  confidence?: number;
  confidenceLevel?: string;
  headline?: string;
  summary?: string;
  /** Verified-safe token flag from backend safe database */
  verifiedSafe?: boolean;
  /** Source/reason for verified-safe designation */
  verifiedSafeSource?: string;
}

const RISK_COLORS: Record<string, string> = {
  GREEN: '#27ae60',
  YELLOW: '#f39c12',
  RED: '#e74c3c',
  UNKNOWN: '#95a5a6',
};

const SAFETY_VERDICTS: Record<string, string> = {
  GREEN: 'Low Risk Indicators',
  YELLOW: 'Moderate Risk Indicators',
  RED: 'High Risk Indicators',
  UNKNOWN: 'Unknown',
};

export function SafetySummaryPanel({ data }: { data: SafetySummaryData | null | undefined }) {
  const d = data ?? null;
  if (!d) return null;

  const riskExplain = getBeginnerRiskLabel(d.riskLevel);
  const riskColor = RISK_COLORS[d.riskLevel] ?? RISK_COLORS.UNKNOWN;
  const verdict = SAFETY_VERDICTS[d.riskLevel] ?? SAFETY_VERDICTS.UNKNOWN;
  const hasConfidence = typeof d.confidence === 'number';
  const cl = hasConfidence ? getConfidenceLabel(d.confidence!) : null;

  return (
    <div className="safety-summary-panel" style={{ borderColor: riskColor }}>
      {/* Verdict — largest, centered, most visible */}
      <div className="safety-summary-verdict-row">
        <span className="safety-summary-verdict-badge" style={{ color: riskColor, borderColor: riskColor }}>
          {verdict}
        </span>
      </div>

      {/* Verified safe context — show when verified AND risk indicators present */}
      {d.verifiedSafe && (d.riskLevel === 'RED' || d.riskLevel === 'YELLOW') && (
        <div className="safety-summary-verified-context">
          <strong>Operational Risk Indicators Detected</strong>
          <p>
            This token is verified, but automated forensic analysis detected risk
            indicators related to operational wallet behavior, liquidity structure,
            or contract characteristics. This does not necessarily indicate
            malicious intent.
          </p>
        </div>
      )}

      {/* Scores row */}
      <div className="safety-summary-scores-row">
        <div className="safety-summary-score-block">
          <span className="safety-summary-score-label">
            Risk Assessment Score
            <InfoTooltip explain={METRIC_EXPLANATIONS.riskScore} compact />
          </span>
          <span className="safety-summary-score-num" style={{ color: riskColor }}>
            {d.score.toFixed(1)} / 10
          </span>
        </div>
        {hasConfidence && cl && (
          <div className="safety-summary-score-block">
            <span className="safety-summary-score-label">
              Analysis Confidence
              <InfoTooltip explain={METRIC_EXPLANATIONS.confidence} compact />
            </span>
            <span className="safety-summary-score-num" style={{ color: cl.color }}>
              {Math.round(d.confidence!)}% ({cl.label})
            </span>
          </div>
        )}
      </div>

      {/* Legal disclaimer below score */}
      <p style={{ margin: '0.25rem 0 0.5rem', fontSize: '0.65rem', color: 'var(--text-muted)', textAlign: 'center', lineHeight: 1.4, opacity: 0.8 }}>
        This assessment reflects automated risk indicators, does not eliminate risk, and should not be interpreted as assurance.
      </p>

      {/* Progress bar */}
      <div className="safety-summary-bar">
        <div
          className="safety-summary-bar-fill"
          style={{
            width: `${Math.min(100, d.score * 10)}%`,
            background: riskColor,
          }}
        />
      </div>

      {/* What This Means */}
      <div className="safety-summary-explain-section">
        <h4 className="safety-summary-explain-heading">What This Means</h4>
        <p className="safety-summary-explain-text">{riskExplain.shortExplain}</p>
      </div>

      {/* What You Should Do */}
      <div className="safety-summary-explain-section">
        <h4 className="safety-summary-explain-heading">What You Should Do</h4>
        <p className="safety-summary-explain-text">{riskExplain.advice}</p>
      </div>

      {/* AI Summary (if available) */}
      {d.summary && (
        <div className="safety-summary-ai-summary">
          <p>{d.summary}</p>
        </div>
      )}
    </div>
  );
}
